/**
 * Composite ID Utilities
 *
 * Utilities for working with composite IDs that include deck references.
 *
 * Format: d{index}|slide_{id}|optional_suffix
 * Examples:
 * - "d0|slide_257" - slide 257 in deck 0
 * - "d0|slide_257|read_complete" - complete read result for slide 257 in deck 0
 * - "d1|layout_2147483951" - layout in deck 1
 *
 * Uses | as separator because:
 * - : appears in Excel range addresses (A1:B10) and time formats
 * - | never appears in natural identifiers
 *
 * Storage: Always stores with full deck ID internally
 * - LLM sees/uses: "d0|slide_257"
 * - Internal storage: "deck_abc123-def456|slide_257"
 */
import { getAgentServices } from '../services';
import { getContextResolver } from './contextResolver';
// Separator character for composite IDs
// Using | because it never appears in natural identifiers (Excel ranges, sheet names, etc.)
export const COMPOSITE_ID_SEPARATOR = '|';
// ═══════════════════════════════════════════════════════════════════════════
// PARSING
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Parse a composite ID into its components
 *
 * @param compositeId - Composite ID to parse (e.g., "d0|slide_257|read_complete")
 * @returns Parsed components or null if invalid format
 *
 * @example
 * parseCompositeId("d0|slide_257")
 * // → { deckRef: "d0", contentId: "slide_257", fullId: "d0|slide_257" }
 *
 * parseCompositeId("d0|slide_257|read_complete")
 * // → { deckRef: "d0", contentId: "slide_257", suffix: "read_complete", fullId: "d0|slide_257|read_complete" }
 */
export function parseCompositeId(compositeId) {
    if (!compositeId || typeof compositeId !== 'string') {
        return null;
    }
    const parts = compositeId.split(COMPOSITE_ID_SEPARATOR);
    // Must have at least deckRef|contentId
    if (parts.length < 2) {
        return null;
    }
    const [deckRef, contentId, ...rest] = parts;
    const suffix = rest.length > 0 ? rest.join(COMPOSITE_ID_SEPARATOR) : undefined;
    return {
        deckRef,
        contentId,
        suffix,
        fullId: compositeId,
    };
}
/**
 * Check if a string is a composite ID (contains deck reference)
 *
 * @param id - String to check
 * @returns True if it looks like a composite ID
 */
export function isCompositeId(id) {
    if (!id || typeof id !== 'string') {
        return false;
    }
    // Check if it matches the pattern of having a | separator
    // and starts with a deck reference pattern (d\d+, CURRENT, or deck_)
    const hasSeparator = id.includes(COMPOSITE_ID_SEPARATOR);
    const startsWithDeckRef = /^(d\d+|CURRENT|deck_)/i.test(id);
    return hasSeparator && startsWithDeckRef;
}
// ═══════════════════════════════════════════════════════════════════════════
// RESOLUTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Resolve a composite ID to its full deck ID and storage key
 *
 * Two-layer resolution:
 * 1. Resolve deck reference (d0 → deck_abc123...)
 * 2. Build internal storage key with full deck ID
 *
 * @param compositeId - Composite ID (e.g., "d0|slide_257|read_complete")
 * @returns Resolved composite ID or null if invalid/deck not found
 *
 * @example
 * resolveCompositeId("d0|slide_257")
 * // → {
 * //   original: "d0|slide_257",
 * //   documentId: "deck_abc123-def456...",
 * //   deckAlias: "d0",
 * //   contentId: "slide_257",
 * //   storageKey: "deck_abc123-def456|slide_257"
 * // }
 */
export function resolveCompositeId(compositeId) {
    const parsed = parseCompositeId(compositeId);
    if (!parsed) {
        console.warn(`[CompositeId] Invalid composite ID format: ${compositeId}`);
        return null;
    }
    const deckRegistry = getAgentServices().contextProviders?.getDocumentRegistry?.();
    // Resolve deck reference to full deck ID
    const documentId = deckRegistry.resolveDeckRef(parsed.deckRef);
    if (!documentId) {
        console.warn(`[CompositeId] Could not resolve deck reference: ${parsed.deckRef}`);
        return null;
    }
    // Get deck alias for the resolved deck
    const deckAlias = deckRegistry.getDeckAlias(documentId) || parsed.deckRef;
    // Build storage key with full deck ID
    const storageKeyParts = [documentId, parsed.contentId];
    if (parsed.suffix) {
        storageKeyParts.push(parsed.suffix);
    }
    const storageKey = storageKeyParts.join(COMPOSITE_ID_SEPARATOR);
    return {
        original: compositeId,
        documentId,
        deckAlias,
        contentId: parsed.contentId,
        suffix: parsed.suffix,
        storageKey,
    };
}
/**
 * Resolve composite ID and retrieve content from context resolver
 *
 * @param compositeId - Composite ID to resolve and retrieve
 * @returns Resolved content or null if not found
 */
export function resolveCompositeContext(compositeId) {
    const resolved = resolveCompositeId(compositeId);
    if (!resolved) {
        return null;
    }
    const contextResolver = getContextResolver();
    return contextResolver.resolve(resolved.storageKey);
}
// ═══════════════════════════════════════════════════════════════════════════
// STORAGE
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Store content with a composite ID
 *
 * - Accepts LLM-friendly ID (d0|slide_257)
 * - Stores with full deck ID internally
 * - Returns the LLM-friendly ID
 *
 * @param compositeId - Composite ID (e.g., "d0|slide_257|read_complete")
 * @param content - Content to store
 * @returns The composite ID (same as input) or null if invalid
 *
 * @example
 * storeCompositeContext("d0|slide_257|read_complete", slideData)
 * // Internally stores at: "deck_abc123-def456|slide_257|read_complete"
 * // Returns: "d0|slide_257|read_complete"
 */
export function storeCompositeContext(compositeId, content) {
    const resolved = resolveCompositeId(compositeId);
    if (!resolved) {
        console.warn(`[CompositeId] Cannot store - invalid composite ID: ${compositeId}`);
        return null;
    }
    const contextResolver = getContextResolver();
    contextResolver.storeWithId(resolved.storageKey, content);
    console.log(`[CompositeId] Stored at ${resolved.storageKey} (LLM ref: ${compositeId})`);
    return compositeId;
}
/**
 * Create a composite ID from deck ID and content ID
 *
 * @param documentId - Full deck ID
 * @param contentId - Content ID (e.g., "slide_257")
 * @param suffix - Optional suffix (e.g., "read_complete")
 * @returns Composite ID with deck alias (e.g., "d0|slide_257|read_complete")
 *
 * @example
 * createCompositeId("deck_abc123...", "slide_257", "read_complete")
 * // → "d0|slide_257|read_complete"
 */
export function createCompositeId(documentId, contentId, suffix) {
    const deckRegistry = getAgentServices().contextProviders?.getDocumentRegistry?.();
    const deckAlias = deckRegistry.getDeckAlias(documentId) || documentId;
    const parts = [deckAlias, contentId];
    if (suffix) {
        parts.push(suffix);
    }
    return parts.join(COMPOSITE_ID_SEPARATOR);
}
/**
 * Create and store content with a composite ID in one operation
 *
 * @param documentId - Full deck ID
 * @param contentId - Content ID
 * @param content - Content to store
 * @param suffix - Optional suffix
 * @returns The created composite ID
 *
 * @example
 * createAndStoreCompositeContext("deck_abc...", "slide_257", slideData, "read_complete")
 * // Stores at: "deck_abc...|slide_257|read_complete"
 * // Returns: "d0|slide_257|read_complete"
 */
export function createAndStoreCompositeContext(documentId, contentId, content, suffix) {
    const compositeId = createCompositeId(documentId, contentId, suffix);
    const resolved = resolveCompositeId(compositeId);
    if (!resolved) {
        throw new Error(`Failed to resolve created composite ID: ${compositeId}`);
    }
    const contextResolver = getContextResolver();
    contextResolver.storeWithId(resolved.storageKey, content);
    console.log(`[CompositeId] Created and stored ${compositeId} at ${resolved.storageKey}`);
    return compositeId;
}
